import { useEffect, useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { sanitizeDescription } from '@/lib/sanitizeDescription';
import {
  Phone, Mail, MessageCircle, Clock, BedDouble, Bath, MapPin, CalendarClock,
  ArrowLeft, ChevronLeft, ChevronRight, Loader2,
  Wifi, Snowflake, UtensilsCrossed, WashingMachine, Wind, Tv, Waves,
  Bath as BathIcon, Car, Dumbbell, ArrowUpDown, Fence, Umbrella, PawPrint,
  Coffee, Sparkles, Shirt, Flame, ShieldCheck, Laptop,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { getPublicCollection, getPublicCollectionListing } from '@/lib/collectionsApi';
import { Toaster } from '@/components/ui/sonner';
import { GoogleMapView } from '@/components/GoogleMapView';

const API = process.env.REACT_APP_BACKEND_URL;
const SHARE_DOMAIN = (process.env.REACT_APP_SHARE_DOMAIN || '').trim().toLowerCase();

const AMENITY_ICONS = {
  'wifi': Wifi, 'snowflake': Snowflake, 'utensils-crossed': UtensilsCrossed,
  'washing-machine': WashingMachine, 'wind': Wind, 'tv': Tv, 'waves': Waves,
  'bath': BathIcon, 'car': Car, 'dumbbell': Dumbbell, 'arrow-up-down': ArrowUpDown,
  'fence': Fence, 'umbrella': Umbrella, 'paw-print': PawPrint, 'coffee': Coffee,
  'sparkles': Sparkles, 'shirt': Shirt, 'flame': Flame, 'shield-check': ShieldCheck,
  'laptop': Laptop,
};

function AmenityIcon({ icon, className = 'h-5 w-5' }) {
  const IconComp = AMENITY_ICONS[icon] || Sparkles;
  return <IconComp className={className} />;
}

/**
 * Base path for a collection on the current domain.
 *  - On the share domain (e.g. flexlivingsf.com): `/<slug>`
 *  - On the main / preview domain:               `/c/<slug>`
 */
function collectionBasePath(slug) {
  const host = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const onShareDomain = SHARE_DOMAIN && host === SHARE_DOMAIN;
  return onShareDomain ? `/${slug}` : `/c/${slug}`;
}

const PLACEHOLDER_IMAGES = [
  'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1200&h=900&fit=crop&q=90',
  'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=1200&h=900&fit=crop&q=90',
  'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1200&h=900&fit=crop&q=90',
  'https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=1200&h=900&fit=crop&q=90',
];

const resolvePhotoUrl = (photo) => {
  if (!photo?.url) return null;
  return photo.url.startsWith('http') ? photo.url : `${API}${photo.url}`;
};

export function CollectionHeader({ business_heading }) {
  return (
    <header className="bg-[#064e3b] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center gap-3" data-testid="collection-header">
        <div className="h-10 w-10 rounded-lg bg-[#064e3b] border border-emerald-400/40 flex items-center justify-center flex-shrink-0" aria-hidden>
          <svg viewBox="0 0 512 512" className="h-6 w-6">
            <rect x="112" y="112" width="288" height="52" rx="14" fill="#34d399"/>
            <rect x="112" y="230" width="220" height="52" rx="14" fill="#10b981"/>
            <rect x="112" y="112" width="52" height="288" rx="14" fill="#6ee7b7"/>
          </svg>
        </div>
        <h1 className="font-heading text-lg sm:text-xl font-bold tracking-tight truncate" data-testid="collection-business-heading">
          {business_heading || 'Flex Living'}
        </h1>
      </div>
    </header>
  );
}

export function ContactFooter({ contact }) {
  const c = contact || {};
  const hasName = !!(c.name || '').trim();
  return (
    <footer className="border-t bg-[#fafaf7] mt-12" data-testid="collection-contact-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center">
        <p className="text-sm text-muted-foreground">Please reach out to {hasName ? <span className="font-semibold text-foreground">{c.name}</span> : 'us'} with any questions.</p>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm">
          {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1.5 hover:text-primary" data-testid="contact-phone"><Phone className="h-4 w-4" />{c.phone}</a>}
          {c.whatsapp && <a href={`https://wa.me/${c.whatsapp.replace(/[^0-9+]/g, '')}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-primary" data-testid="contact-whatsapp"><MessageCircle className="h-4 w-4" />WhatsApp</a>}
          {c.email && <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 hover:text-primary" data-testid="contact-email"><Mail className="h-4 w-4" />{c.email}</a>}
          {c.best_times && <span className="inline-flex items-center gap-1.5 text-muted-foreground" data-testid="contact-times"><Clock className="h-4 w-4" />{c.best_times}</span>}
        </div>
      </div>
    </footer>
  );
}

function NotFound({ slug }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6 text-center">
      <div>
        <h1 className="font-heading text-2xl font-bold">Link not found</h1>
        <p className="text-sm text-muted-foreground mt-2">The link <span className="font-mono">/c/{slug}</span> is no longer active or the URL was mistyped.</p>
      </div>
    </div>
  );
}

function BedBathIcons({ bedrooms, bathrooms }) {
  return (
    <div className="flex items-center gap-3 mt-1.5 text-muted-foreground" data-testid="bed-bath-icons">
      {bedrooms != null && (
        <span className="flex items-center gap-1 text-sm">
          <BedDouble className="h-3.5 w-3.5" />{bedrooms === 0 ? 'Studio' : `${bedrooms} Bed`}
        </span>
      )}
      {bathrooms != null && (
        <span className="flex items-center gap-1 text-sm">
          <Bath className="h-3.5 w-3.5" />{bathrooms} Bath
        </span>
      )}
    </div>
  );
}

export function NextAvailableChip({ next }) {
  if (!next) {
    return <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 rounded-full px-2 py-0.5 text-[11px] font-medium" data-testid="collection-available-now"><CalendarClock className="h-3 w-3" />Available now</span>;
  }
  return <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 rounded-full px-2 py-0.5 text-[11px] font-medium" data-testid="collection-available-forward"><CalendarClock className="h-3 w-3" />Available {next}</span>;
}

// ═════════════════════════════════════════════════════════════════════════
// Collection index — list of listings attached to this share link
// (Card style mirrors the main ListingsPage.ListingCard so the two stay uniform)
// ═════════════════════════════════════════════════════════════════════════
export function CollectionIndexPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPublicCollection(slug)
      .then(d => { if (!cancelled) { setData(d); } })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (error || !data) return <NotFound slug={slug} />;

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="collection-index-page">
      <CollectionHeader business_heading={data.business_heading} />

      <main className="flex-1">
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4">
          {data.message && (
            <div className="rounded-xl bg-emerald-50/60 border border-emerald-100 px-5 py-4 whitespace-pre-wrap text-[15px] leading-relaxed text-[#1f3b34]" data-testid="collection-message">
              {data.message}
            </div>
          )}
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
          <p className="text-sm text-muted-foreground mb-4" data-testid="collection-listings-count">
            {data.listings.length} listing{data.listings.length === 1 ? '' : 's'}
          </p>
          {data.listings.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">No listings attached to this link yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="collection-listings-grid">
              {data.listings.map((l, i) => (
                <CollectionListingCard key={l.id} listing={l} index={i} slug={slug} />
              ))}
            </div>
          )}
        </section>
      </main>

      <ContactFooter contact={data.contact} />
      <Toaster position="bottom-right" />
    </div>
  );
}

/**
 * Collection listing card — visually identical to the main
 * `ListingsPage.ListingCard`: multi-photo carousel with hover arrows + dots,
 * bed/bath icons, price. The only difference is the destination link, which
 * stays inside the collection namespace.
 */
function CollectionListingCard({ listing, index, slug }) {
  const allImages = listing.photos?.length > 0
    ? listing.photos.map(p => (p.url.startsWith('http') ? p.url : `${API}${p.url}`))
    : [PLACEHOLDER_IMAGES[index % PLACEHOLDER_IMAGES.length]];
  const [currentIdx, setCurrentIdx] = useState(0);
  const goPrev = (e) => { e.preventDefault(); e.stopPropagation(); setCurrentIdx(i => (i - 1 + allImages.length) % allImages.length); };
  const goNext = (e) => { e.preventDefault(); e.stopPropagation(); setCurrentIdx(i => (i + 1) % allImages.length); };
  const detailUrl = `${collectionBasePath(slug)}/l/${listing.id}`;

  return (
    <div className="group bg-card rounded-xl border border-border/70 overflow-hidden transition-shadow duration-300 hover:shadow-lg" data-testid="collection-listing-card">
      <div className="aspect-[4/3] overflow-hidden bg-muted relative">
        <Link to={detailUrl} data-testid="collection-listing-card-link">
          <img src={allImages[currentIdx]} alt={listing.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" loading="lazy" decoding="async" />
        </Link>
        {allImages.length > 1 && (
          <>
            <button onClick={goPrev} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center shadow-md hover:bg-white transition-all opacity-0 group-hover:opacity-100 z-10" data-testid="collection-card-carousel-prev"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={goNext} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center shadow-md hover:bg-white transition-all opacity-0 group-hover:opacity-100 z-10" data-testid="collection-card-carousel-next"><ChevronRight className="h-4 w-4" /></button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10">
              {allImages.map((img, i) => <div key={img || `img-${i}`} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === currentIdx ? 'bg-white' : 'bg-white/50'}`} />)}
            </div>
          </>
        )}
      </div>
      <Link to={detailUrl}>
        <div className="p-5">
          <h3 className="font-heading text-lg font-semibold tracking-tight" data-testid="collection-listing-title">{listing.title}</h3>
          <BedBathIcons bedrooms={listing.bedrooms} bathrooms={listing.bathrooms} />
          <div className="mt-2 flex items-center justify-between gap-2">
            {listing.current_price != null ? (
              <p className="text-primary font-semibold text-lg tabular-nums" data-testid="collection-listing-price">${listing.current_price.toLocaleString()}/mo</p>
            ) : (
              <p className="text-muted-foreground text-sm">Contact for pricing</p>
            )}
            <NextAvailableChip next={listing.next_available} />
          </div>
        </div>
      </Link>
    </div>
  );
}

/* ── Photo Lightbox ──────────────────────────────────────────── */
function PhotoLightbox({ open, onOpenChange, photos, startIdx = 0 }) {
  const [idx, setIdx] = useState(startIdx);
  useEffect(() => { if (open) setIdx(startIdx); }, [open, startIdx]);
  if (!photos?.length) return null;
  const goPrev = () => setIdx(i => (i - 1 + photos.length) % photos.length);
  const goNext = () => setIdx(i => (i + 1) % photos.length);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl p-0 bg-black/95 border-0 overflow-hidden" style={{ zIndex: 100 }} data-testid="collection-photo-lightbox">
        <DialogTitle className="sr-only">Photo {idx + 1} of {photos.length}</DialogTitle>
        <div className="relative flex items-center justify-center" style={{ minHeight: '70vh' }}>
          <img src={photos[idx]} alt="" className="max-h-[80vh] max-w-full object-contain" />
          {photos.length > 1 && (
            <>
              <button onClick={goPrev} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors" data-testid="collection-lightbox-prev">
                <ChevronLeft className="h-5 w-5 text-white" />
              </button>
              <button onClick={goNext} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors" data-testid="collection-lightbox-next">
                <ChevronRight className="h-5 w-5 text-white" />
              </button>
            </>
          )}
          <div className="absolute top-3 right-3 bg-black/50 text-white rounded-full px-3 py-1 text-xs font-medium">{idx + 1} / {photos.length}</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Listing detail inside a collection
// (Layout mirrors the main ListingDetailPage: left column = photos on top +
// map stretched below; right column = info + description; bottom = compact
// centered amenities.)
// ═════════════════════════════════════════════════════════════════════════
export function CollectionListingPage() {
  const { slug, listingId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentPhotoIdx, setCurrentPhotoIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPublicCollectionListing(slug, listingId)
      .then(d => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug, listingId]);

  const photoSrcs = useMemo(() => {
    if (!data?.listing?.photos?.length) return [PLACEHOLDER_IMAGES[0]];
    return data.listing.photos.map(resolvePhotoUrl).filter(Boolean);
  }, [data]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (error || !data) return <NotFound slug={slug} />;

  const l = data.listing;
  const openLightbox = (idx) => { setLightboxIdx(idx); setLightboxOpen(true); };
  const videoUrl = l.video?.url ? (l.video.url.startsWith('http') ? l.video.url : `${API}${l.video.url}`) : null;

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="collection-listing-page">
      <CollectionHeader business_heading={data.business_heading} />

      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <button onClick={() => navigate(collectionBasePath(slug))} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="collection-back-arrow">
            <ArrowLeft className="h-4 w-4" /> Back to all listings
          </button>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* ─── Two-column layout: Photos+Map (left) stretch to match Details (right) ─── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
            {/* ── Left Column: Photo Gallery (top) + Map (fills remaining height) ── */}
            <div className="flex flex-col gap-6" data-testid="collection-left-column">
              <div className="relative flex-shrink-0" data-testid="collection-image-carousel">
                <div className="aspect-[4/3] rounded-xl overflow-hidden bg-muted relative cursor-pointer" onClick={() => openLightbox(currentPhotoIdx)}>
                  <img src={photoSrcs[currentPhotoIdx]} alt={l.title} className="w-full h-full object-cover" decoding="async" />
                  {photoSrcs.length > 1 && (
                    <>
                      <button onClick={(e) => { e.stopPropagation(); setCurrentPhotoIdx(i => (i - 1 + photoSrcs.length) % photoSrcs.length); }}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center shadow-md hover:bg-white transition-colors" data-testid="collection-carousel-prev">
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); setCurrentPhotoIdx(i => (i + 1) % photoSrcs.length); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center shadow-md hover:bg-white transition-colors" data-testid="collection-carousel-next">
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </>
                  )}
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {photoSrcs.slice(0, 10).map((_, i) => (
                      <button key={i} onClick={(e) => { e.stopPropagation(); setCurrentPhotoIdx(i); }}
                        className={`w-2.5 h-2.5 rounded-full transition-colors ${i === currentPhotoIdx ? 'bg-white shadow' : 'bg-white/50'}`} />
                    ))}
                    {photoSrcs.length > 10 && <span className="text-white text-xs ml-1">+{photoSrcs.length - 10}</span>}
                  </div>
                  <div className="absolute top-3 right-3 bg-black/50 text-white rounded-full px-2.5 py-0.5 text-xs font-medium">{currentPhotoIdx + 1} / {photoSrcs.length}</div>
                </div>
                {/* Thumbnail strip */}
                {photoSrcs.length > 1 && (
                  <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
                    {photoSrcs.map((src, i) => (
                      <button key={i} onClick={() => openLightbox(i)}
                        className={`relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${i === currentPhotoIdx ? 'border-primary' : 'border-transparent hover:border-border'}`}>
                        <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Map — stretches to fill remaining column height */}
              <div className="flex flex-col flex-1 min-h-[320px]" data-testid="collection-map-column">
                <h3 className="font-heading text-lg font-semibold mb-3 flex items-center gap-1.5 flex-shrink-0"><MapPin className="h-5 w-5 text-primary" />Location</h3>
                {l.address && l.address_lat && l.address_lng ? (
                  <div className="rounded-xl overflow-hidden border border-border/70 flex-1" style={{ position: 'relative', zIndex: 0 }} data-testid="collection-location-map">
                    <GoogleMapView lat={l.address_lat} lng={l.address_lng} zoom={13} title={l.address} />
                  </div>
                ) : (
                  <div className="w-full flex-1 rounded-xl border border-dashed border-border/70 bg-muted/30 flex items-center justify-center text-sm text-muted-foreground min-h-[320px]">
                    <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />No map location set</span>
                  </div>
                )}
              </div>
            </div>

            {/* ── Right Column: Unit Info + Description ── */}
            <div className="space-y-5" data-testid="collection-listing-info">
              {(l.bedrooms != null || l.bathrooms != null) && (
                <div className="flex items-center gap-3 text-muted-foreground">
                  {l.bedrooms != null && (
                    <span className="flex items-center gap-1.5 bg-muted rounded-full px-3 py-1 text-xs font-medium">
                      <BedDouble className="h-3.5 w-3.5" />{l.bedrooms === 0 ? 'Studio' : `${l.bedrooms} Bed`}
                    </span>
                  )}
                  {l.bathrooms != null && (
                    <span className="flex items-center gap-1.5 bg-muted rounded-full px-3 py-1 text-xs font-medium">
                      <Bath className="h-3.5 w-3.5" />{l.bathrooms} Bath
                    </span>
                  )}
                  <NextAvailableChip next={l.next_available} />
                </div>
              )}
              <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight" data-testid="collection-listing-detail-title">{l.title}</h1>
              {l.address && (
                <p className="text-muted-foreground flex items-center gap-1.5" data-testid="collection-listing-address"><MapPin className="h-4 w-4" />{l.address}</p>
              )}
              {l.current_price != null ? (
                <p className="text-primary font-bold text-2xl tabular-nums" data-testid="collection-listing-detail-price">${l.current_price.toLocaleString()}/mo</p>
              ) : (
                <p className="text-muted-foreground">Contact for pricing</p>
              )}

              {l.description && (
                <div
                  className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap [&_*]:text-inherit [&_strong]:font-semibold [&_b]:font-semibold [&_p]:mb-2 [&_p:last-child]:mb-0"
                  dangerouslySetInnerHTML={{ __html: sanitizeDescription(l.description) }}
                  data-testid="collection-listing-description"
                />
              )}
            </div>
          </div>

          {/* ─── Amenities — full width, compact pills (mirrors main listing page) ─── */}
          {l.amenities?.length > 0 && (
            <div className="mt-10" data-testid="collection-amenities-section">
              <h3 className="font-heading text-lg font-semibold mb-4 text-center">Amenities</h3>
              <div className="flex flex-wrap gap-2 justify-center">
                {l.amenities.map((amenity, i) => (
                  <div key={amenity.name || i} className="inline-flex items-center gap-1.5 bg-muted/60 rounded-full px-3 py-1.5 text-xs">
                    <AmenityIcon icon={amenity.icon} className="h-3.5 w-3.5 text-foreground/80" />
                    <span>{amenity.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ─── Video Section ─────────────────────────────────── */}
          {videoUrl && (
            <div className="mt-8" data-testid="collection-video-section">
              <h3 className="font-heading text-lg font-semibold mb-3">Property Video</h3>
              <div className="rounded-xl overflow-hidden border border-border/70 bg-black max-w-3xl">
                <video controls className="w-full" preload="metadata" style={{ maxHeight: '500px' }}>
                  <source src={videoUrl} type={l.video?.content_type || 'video/mp4'} />
                </video>
              </div>
            </div>
          )}
        </div>
      </main>

      <PhotoLightbox
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        photos={photoSrcs}
        startIdx={lightboxIdx}
      />

      <ContactFooter contact={data.contact} />
      <Toaster position="bottom-right" />
    </div>
  );
}
